import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Grid, Typography, Box, CardContent, CardHeader, Card, Stack, Divider, Button, Tooltip, FormHelperText, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useLocation } from 'react-router-dom';
import { Document } from '../../../types/DocumentType';
import axios from '../../../../services/ApiService';
import { Company } from '../../../types/UserTypes';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../support/LoadingSpinner';
import Swal from 'sweetalert2'
import { BVTConstants } from '../../../../utils/BVTConstants';
import { useDocuments } from '../../../../hooks/useDocuments';
import AdminCompanyBusinessView from './AdminCompanyBusinessView';
import { paths } from '../../../../utils/paths';
import CompanyServiceInfo from '../../agent/company/CompanyServiceInfo';
import CompanyPlatformInfo from '../../agent/company/CompanyPlatformInfo';
import CompanySubscriptions from '../../agent/company/CompanySubscriptions';

function AdminViewCompany() {
  const navigate = useNavigate();
  const location = useLocation();
  const companyId = (location.state as { companyId?: number })?.companyId;
  const { showDocument, saveDocument, getDocumentUrl } = useDocuments();
  const [error, setError] = useState<string>('');
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [verify, setVerify] = useState<boolean>(false);
  const [verificationComment, setVerificationComment] = useState('');
  const [verCommentError, setVerCommentError] = useState('');
  const [certError, setCertError] = useState('');
  const [companyData, setCompanyData] = useState<Company | null>(null);
  const [certificateData, setCertificateData] = useState<Document>({
    id: 0,
    fileName: '',
    purpose: '',
    contentFile: null,
    contentBytes: null,
    status: '',
    mimeType: '',
    createdAt: '',
    updatedAt: '',
  });
  const [logoData, setLogoData] = useState<Document>({
    id: 0,
    fileName: '',
    purpose: '',
    contentFile: null,
    contentBytes: null,
    status: '',
    mimeType: '',
    createdAt: '',
    updatedAt: '',
  });
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  
  useEffect(() => {
    fetchCompanyData();
  }, []);

  useEffect(() => {
    const fetchLogo = async () => {
      if (companyData?.logoId) {
        try {
          const url = await getDocumentUrl(companyData.logoId);
          setLogoUrl(url);
        } catch (error) {
          console.error('Error fetching logo:', error);
        }
      }
    };
    fetchLogo();
  }, [companyData?.logoId]);

  const fetchCompanyData = async () => {
    try {
      setDataLoading(true);
      setVerify(false);
      const response = await axios.post("/getCompany", companyId);
      setCompanyData(response.data);
    } catch (error) {
      console.error('Error fetching company data.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleVerification = async () => {
    if(String(companyData?.isVerified) === 'true' && companyData?.certId) {
      setLoading(true);
      await showDocument(companyData?.certId);
      setLoading(false);
    } else {
      setVerificationComment('');
      setVerify(true);
    }
  }

  const saveVerification = async () => {
    setVerCommentError('');
    setCertError('');
    if (!verificationComment.trim()) {
      setVerCommentError('Verification comment is required.');
      return;
    }
    if (!certificateData.contentFile) {
      setCertError('Certificate is required.');
      return;
    }
    setLoading(true);
    try{
      const docId = await saveDocument(certificateData);
      console.log('Document saved successfully ' + docId);
      companyData.certId = docId;
      companyData.isVerified = true;
      companyData.verificationComment = verificationComment;
      await axios.post("/updateCompanyCertificate", companyData);
      toast.success("Company verified successfully.");
      fetchCompanyData();
    }catch (error) {
      console.error('Error saving company.');
    } finally {
      setLoading(false);
    }
  }

  const handleDefault = async () => {
    Swal.fire({title: 'Change Default Agent ?', 
      text: 'Do you want to make the company ' + companyData?.companyName + ' as default agent?', 
      icon: 'question', 
      confirmButtonText: 'Yes', 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        updateDefault(true);
      }
    })
  }

  const updateDefault = async (defalut: boolean) => {
    setLoading(true);
    try{
      companyData.isDefault = defalut;
      await axios.post("/updateCompanyProfile", companyData);
      toast.success("Company updated successfully.");
      fetchCompanyData();
    }catch (error) {
      console.error('Error updating company.');
    } finally {
      setLoading(false);
    } 
  }

  const updateStatusClick = async () => {
    const activeText: string = companyData?.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate';
    Swal.fire({title: activeText + ' Company ?', 
      text: 'Do you want to '+ activeText +' the company ' + companyData?.companyName + '?', 
      icon: 'question', 
      confirmButtonText: activeText, 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        const req = {id: companyData.userId, status: companyData.status === BVTConstants.ACTIVE ? BVTConstants.INACTIVE : BVTConstants.ACTIVE}
        changeStatus(req);
      }
    })
  }

  const archiveCompany = async () => {
    Swal.fire({title: 'Archive Company ?', 
      text: 'Do you want to archive the company ' + companyData?.companyName + '?', 
      icon: 'question', 
      confirmButtonText: 'Archive', 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        const req = {id: companyData.userId, status: BVTConstants.ARCHIVED}
        changeStatus(req);
      }
    })
  }

  const changeStatus = async (req: any) => {
    try {
      setLoading(true);
      const response = await axios.post("/updateUserStatus", req);
      if(response.status === 200){
        toast.success('Company updated successfully');
        if(req.status === BVTConstants.ARCHIVED){
          navigate(-1);
        }else{
          fetchCompanyData();
        }
      }
    } catch (error) {
      console.error('Error updating company.');
    } finally {
      setLoading(false);
    }
  }

  const handleLogoUpdate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, JPG, or PNG)');
        return;
      }

      if (file.size > 1024 * 1024) { // 1MB limit (1024 * 1024 bytes)
        toast.error('File size should not exceed 1 MB');
        return;
      }

      setLoading(true);
      try {
        const documentData = {
          ...logoData,
          contentFile: file,
          fileName: file.name,
          purpose: BVTConstants.COMPANY_LOGO
        };

        setLogoData(documentData);

        const docId = await saveDocument(documentData);
        
        if (companyData) {
          const updatedCompanyData = {
            ...companyData,
            logoId: docId
          };
          await axios.post("/updateCompanyLogo", updatedCompanyData);
          setCompanyData(updatedCompanyData);
          
          const newLogoUrl = await getDocumentUrl(docId);
          setLogoUrl(newLogoUrl);
          
          toast.success("Company logo updated successfully");
        }
      } catch (error) {
        console.error('Error updating logo');
        toast.error("Failed to update logo");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
    <Helmet><title>BVT Admin - Companies</title></Helmet>
    {dataLoading ? (
          <LoadingSpinner loading={dataLoading} />
        ) : error ? (
          <Typography variant="h6" color="error" textAlign="center">
            {error}
          </Typography>
        ) : (
        <Stack spacing={2}>
          <Card>
            <CardHeader 
              title={companyData?.companyName}
              subheader={String(companyData?.isDefault) === 'true' ? `${companyData?.status}-DEFAULT` : companyData?.status}
              action={
                <>
                  {String(companyData?.isDefault) === 'false' && companyData?.status === BVTConstants.ACTIVE && (
                    <Button
                      variant="outlined"
                      sx={{marginRight: 1}}
                      onClick={() => handleDefault()}
                    >
                      Set Default
                  </Button>
                  )}
                  <Button
                      variant="outlined"
                      sx={{marginRight: 1}}
                      onClick={() => handleVerification()}
                    >
                      {String(companyData?.isVerified) === 'true' ? 'View Certificate' : 'Verify Company'}
                  </Button>
                  {String(companyData?.isDefault) === 'false' && (
                  <Button
                      variant="outlined"
                      sx={{marginRight: 1}}
                      onClick={() => updateStatusClick()}
                    >
                      {companyData?.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate'}
                  </Button>
                  )}
                  {String(companyData?.isDefault) === 'false' && (
                  <Tooltip title="Archive Company" arrow>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => archiveCompany()}
                      >
                        <DeleteOutlineIcon/>
                      </Button>
                  </Tooltip>
                  )}
                  
                  <IconButton 
                    aria-label="edit" 
                    onClick={() => navigate(paths.admin.editCompanyBase, { state: { companyId: companyData?.companyId } })}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'divider',
                      ml: 1
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    aria-label="close" 
                    onClick={() => navigate(-1)}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'divider',
                      ml: 1
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </>
              }
              />
            <Divider />
            <CardContent>
              <Box sx={{ maxWidth: '900px' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={10}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Company Name
                        </Typography>
                        <Typography variant="body1">{companyData?.companyName}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Agent ABN
                        </Typography>
                        <Typography variant="body1">{companyData?.agentABN}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Contact First Name
                        </Typography>
                        <Typography variant="body1">{companyData?.firstName}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Contact Last Name
                        </Typography>
                        <Typography variant="body1">{companyData?.lastName}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Email
                        </Typography>
                        <Typography variant="body1">{companyData?.email}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Contact No.
                        </Typography>
                        <Typography variant="body1">{companyData?.contact}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Web Link
                        </Typography>
                        <Tooltip title={companyData?.webLink || 'N/A'}>
                        <Typography variant="body1">
                          {companyData?.webLink ? (
                            <a 
                              href={companyData.webLink.startsWith('http') ? companyData.webLink : `https://${companyData.webLink}`}
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ color: 'inherit', textDecoration: 'none' }}
                            >
                              {companyData.webLink.length > 35 
                                    ? `${companyData.webLink.substring(0, 35)}...` 
                                    : companyData.webLink}
                            </a>
                          ) : (
                            'N/A'
                          )}
                        </Typography>
                        </Tooltip>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Office Location
                        </Typography>
                        <Typography variant="body1">{companyData?.address}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Member Since
                        </Typography>
                        <Typography variant="body1">{companyData?.createdAt}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Last Login
                        </Typography>
                        <Typography variant="body1">{companyData?.lastLoginAt}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Is Verified
                        </Typography>
                        <Typography variant="body1">{String(companyData?.isVerified) === 'true' ? 'Yes' : 'No'}</Typography>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Verification Comment
                        </Typography>
                        <Typography variant="body1">{companyData?.verificationComment ? companyData?.verificationComment : 'N/A'}</Typography>
                      </Grid>

                      {verify && (
                        <>
                        <Divider />
                        <Grid item xs={12} md={7} mt={1}>
                            <FormControl fullWidth error={!!verCommentError}>
                                    <InputLabel>Verification Comment</InputLabel>
                                    <OutlinedInput 
                                        label="Verification Comment" 
                                        name="verificationComment"
                                        value={verificationComment}
                                        inputProps={{ maxLength: 100 }}
                                        onChange={(event) => setVerificationComment(event.target.value)}
                                    />
                                    {verCommentError && (
                                        <FormHelperText error>{verCommentError}</FormHelperText>
                                    )}
                                </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3} mt={1}>
                            <Button
                                    variant="contained"
                                    component="label"
                                    fullWidth
                                >
                                    {certificateData.fileName ? 'Change File' : 'Upload Certificate'}
                                    <input
                                        type="file"
                                        hidden
                                        accept={BVTConstants.FILE_TYPES}
                                        onChange={(event) => {
                                            if (event.target.files && event.target.files.length > 0) {
                                                const file = event.target.files[0];
                                                
                                                // Check file size
                                                if (file.size > BVTConstants.FILE_SIZE_500_KB_LIMIT) {
                                                    setCertError('File size should not exceed 500 KB');
                                                    return;
                                                }
                                                
                                                // Check file format
                                                const allowedTypes = BVTConstants.FILE_TYPES.split(',');
                                                const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
                                                const isValidFormat = allowedTypes.some(type => 
                                                    type.toLowerCase() === fileExtension
                                                );
                                                
                                                if (!isValidFormat) {
                                                    setCertError(`Invalid file format. Allowed formats: ${BVTConstants.FILE_TYPES}`);
                                                    return;
                                                }
                                                
                                                setCertificateData(prev => ({
                                                    ...prev,
                                                    contentFile: file,
                                                    fileName: file.name,
                                                    purpose: BVTConstants.AGENT_CERTIFICATE
                                                }));
                                                setCertError('');
                                            }
                                        }}
                                    />
                                </Button>
                                {certificateData.fileName && (
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            display: 'block', 
                                            mt: 1, 
                                            wordBreak: 'break-word',
                                            color: 'text.secondary'
                                        }}
                                    >
                                        Selected: {certificateData.fileName}
                                    </Typography>
                                )}
                                {certError && (
                                        <FormHelperText error>{certError}</FormHelperText>
                                    )}
                        </Grid>
                        <Grid item xs={12} md={2} mt={1}>
                            <Button
                                    variant="contained"
                                    component="label"
                                    onClick={() => saveVerification()}
                                    fullWidth
                                >
                                    Verify
                                </Button>
                        </Grid>
                        </>
                        )}
                    </Grid>
                    <Grid item xs={12} md={12} mt={2}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Additional Notes
                        </Typography>
                        <Typography variant="body1">{companyData?.notes ? companyData.notes : 'N/A'}</Typography>
                    </Grid>
                    
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Box 
                      sx={{ 
                        width: '100%', 
                        height: 160, 
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                      }}
                    >
                      {logoUrl ? (
                        <img 
                          src={logoUrl}
                          alt="Company Logo"
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '100%', 
                            objectFit: 'contain' 
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No Logo Available
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                    >
                      Update Logo
                      <input
                        type="file"
                        hidden
                        accept=".jpg,.jpeg,.png"
                        onChange={handleLogoUpdate}
                      />
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
          <CompanyServiceInfo/>
          <CompanyPlatformInfo/>
          <CompanySubscriptions/>
          <AdminCompanyBusinessView/>
          <LoadingSpinner loading={loading} />
        </Stack>
        )}
    
    </>
  )
}

export default AdminViewCompany